import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { fetchAllVenues, deleteVenue } from '../../../services/Http/Api';
import { MdEdit, MdDelete, MdAdd, MdSearch } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import VenueModal from './VenueModal';
import MainButton from '../../../shared/layout/buttons/MainButton';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import VenuesListSkeleton from '../../common/skeletons/VenuesListSkeleton';
import { toast } from 'react-toastify';
import StandardModal from '../../../shared/layout/modals/StandardModal';

const VenuesList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [venues, setVenues] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState('id');
    const [sortDirection, setSortDirection] = useState('asc');
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [activeVenueId, setActiveVenueId] = useState(null);
    const [venueToDelete, setVenueToDelete] = useState(null);
    const [loading, setLoading] = useState(true);

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setPage(1); // Reset to first page on search
        }, 300);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchAllVenues({
                page,
                perPage,
                searchTerm: debouncedSearchTerm,
                sortBy,
                sortDirection
            });

            if (!response) {
                throw new Error('Empty venues response');
            }

            // Handle both paginated and non-paginated responses
            if (Array.isArray(response.data)) {
                setVenues(Array.isArray(response.data) ? response.data : []);
                setTotalRows(response.total || response.data?.length || 0);
            } else if (Array.isArray(response)) {
                setVenues(response);
                setTotalRows(response.length || 0);
            } else {
                setVenues([]);
                setTotalRows(0);
                throw new Error('Unexpected venues response shape');
            }
        } catch (error) {
            console.error('Error fetching venues:', error);
            toast.error(t('Venues.loadError'));
        } finally {
            setLoading(false);
        }
    }, [page, perPage, debouncedSearchTerm, sortBy, sortDirection, t]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleAddNew = () => {
        setModalMode('create');
        setActiveVenueId(null);
        setShowModal(true);
    };

    const handleEditVenue = (id) => {
        setModalMode('edit');
        setActiveVenueId(id);
        setShowModal(true);
    };

    const handleDeleteVenue = async (id) => {
        const selectedVenue = venues.find((venue) => venue.id === id);
        setVenueToDelete(selectedVenue || { id });
    };

    const confirmDeleteVenue = async () => {
        if (!venueToDelete?.id) return;
        try {
            await deleteVenue(venueToDelete.id);
            toast.success(t('Venues.deleteSuccess'));
            setVenueToDelete(null);
            fetchData();
        } catch (error) {
            console.error('Error deleting venue:', error);
            toast.error(t('Venues.deleteError'));
        }
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handlePerRowsChange = (newPerPage, newPage) => {
        setPerPage(newPerPage);
        setPage(newPage);
    };

    const handleSort = (column, direction) => {
        setSortBy(column.sortField || column.key || 'id');
        setSortDirection(direction);
    };

    const columns = useMemo(
        () => [
            {
                name: t('Venues.columns.id'),
                selector: (row) => row.id,
                sortable: true,
                width: '80px',
                sortField: 'id'
            },
            {
                name: t('Venues.columns.name'),
                cell: (row) => (
                    <div
                        style={{ color: 'var(--color-primary)', fontWeight: '600', cursor: 'pointer' }}
                        onClick={() => navigate(`/venues/view/${row.id}`)}
                    >
                        {row.name}
                    </div>
                ),
                sortable: true,
                sortField: 'name'
            },
            {
                name: t('Venues.columns.phone'),
                selector: (row) => {
                    const code = row.phone_country?.phonecode ? `+${row.phone_country.phonecode}` : '';
                    const number = row.phone || '';
                    return code && number ? `${code} ${number}` : `${code}${number}`;
                },
                sortable: true,
                sortField: 'phone'
            },
            {
                name: t('Venues.columns.country'),
                cell: (row) => (
                    <CountryCell>
                        {row.country?.iso && (
                            <img
                                src={`https://flagcdn.com/w40/${row.country.iso.toLowerCase()}.png`}
                                alt={`${row.country.name} flag`}
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                }}
                            />
                        )}
                        <span>{row.country?.name}</span>
                    </CountryCell>
                ),
                sortable: true,
                sortField: 'country_id'
            },
            {
                name: t('Venues.columns.action'),
                cell: (row) => (
                    <ActionButtons>
                        <MdEdit
                            className="edit-icon"
                            onClick={() => handleEditVenue(row.id)}
                        />
                        <MdDelete
                            className="delete-icon"
                            onClick={() => handleDeleteVenue(row.id)}
                        />
                    </ActionButtons>
                ),
                ignoreRowClick: true,
            },
        ],
        [t, navigate]
    );

    const customStyles = {
        table: {
            style: {
                backgroundColor: 'transparent',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#f9fafb',
                minHeight: '52px',
                borderBottom: '1px solid #e5e7eb',
            },
        },
        headCells: {
            style: {
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            },
        },
        rows: {
            style: {
                minHeight: '72px',
                '&:not(:last-of-type)': {
                    borderBottom: '1px solid #f3f4f6',
                },
                '&:hover': {
                    backgroundColor: '#f9fafb !important',
                    transition: 'background-color 0.2s',
                },
            },
        },
    };

    return (
        <>
            <HeaderControls>
                <SearchContainer>
                    <MdSearch size={20} />
                    <input
                        type="text"
                        placeholder={t('Venues.search_placeholder')}
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </SearchContainer>
                <MainButton size="small" onClick={handleAddNew} icon={<MdAdd />}>
                    {t('Venues.add_new_button')}
                </MainButton>
            </HeaderControls>
            <StyledCard noPadding>
                {loading ? (
                    <VenuesListSkeleton />
                ) : (
                    <DataTable
                        columns={columns}
                        data={venues}
                        pagination={totalRows > perPage}
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        onSort={handleSort}
                        sortServer
                        persistTableHead
                        highlightOnHover
                        noHeader
                        customStyles={customStyles}
                        noDataComponent={
                            <div style={{ padding: '1.5rem', color: '#6b7280' }}>
                                {t('Venues.noData')}
                            </div>
                        }
                    />
                )}
            </StyledCard>
            <VenueModal
                show={showModal}
                mode={modalMode}
                venueId={activeVenueId}
                onClose={() => setShowModal(false)}
                onSaved={async () => {
                    setShowModal(false);
                    await fetchData();
                }}
            />
            <StandardModal
                isOpen={Boolean(venueToDelete)}
                onClose={() => setVenueToDelete(null)}
                title={t('Venues.deleteTitle')}
                footer={(
                    <>
                        <MainButton variant="secondary" onClick={() => setVenueToDelete(null)}>
                            {t('common.cancel')}
                        </MainButton>
                        <MainButton variant="danger" onClick={confirmDeleteVenue}>
                            {t('common.delete')}
                        </MainButton>
                    </>
                )}
            >
                <p style={{ margin: 0 }}>
                    {t('Venues.deleteConfirm')}
                    {venueToDelete?.name ? ` (${venueToDelete.name})` : ''}
                </p>
            </StandardModal>
        </>
    );
};

// Styled Components
const HeaderControls = styled.div`
    display: flex;
    gap: 1rem;
    align-items: center;

    @media (max-width: 640px) {
        flex-direction: column;
        align-items: stretch;
        width: 100%;
    }
`;

const SearchContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    background: #f3f4f6;
    border-radius: 12px;
    padding: 0 1rem;
    flex: 1;
    max-width: 400px;
    border: 1.5px solid transparent;
    transition: all 0.2s;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

    &:focus-within {
        background: #fff;
        border-color: var(--color-primary);
        box-shadow: 0 0 0 4px rgba(130, 22, 146, 0.1);
    }

    input {
        background: transparent;
        border: none;
        padding: 0.75rem 0.75rem;
        width: 100%;
        outline: none;
        font-size: 0.875rem;
        color: #111827;

        &::placeholder {
            color: #9ca3af;
        }
    }

    svg {
        color: #6b7280;
        transition: color 0.2s;
    }

    &:focus-within svg {
        color: var(--color-primary);
    }

    @media (max-width: 640px) {
        max-width: none;
    }
`;

const CountryCell = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;

    img {
        width: 24px;
        height: 16px;
        border-radius: 2px;
        object-fit: cover;
        box-shadow: 0 1px 2px rgba(0,0,0,0.1);
    }

    span {
        font-size: 0.875rem;
        color: #1e293b;
        font-weight: 500;
    }
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 0.5rem;

    .edit-icon, .delete-icon {
        width: 32px;
        height: 32px;
        padding: 0.375rem;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
        color: #64748b;

        &:hover {
            background: #f1f5f9;
        }
    }

    .edit-icon:hover {
        color: #821692;
        background: rgba(130, 22, 146, 0.1);
    }

    .delete-icon:hover {
        color: #ef4444;
        background: rgba(239, 68, 68, 0.1);
    }
`;

export default VenuesList;
